import { StoredLogo } from './shared/StoredLogo';

/**
 * SharedSiteEditDialog — Full site-edit dialog shared between CustomersPage and
 * BudgetSiteEditDialog. Edit-only (requires an existing siteId).
 *
 * Mirrors the exact field set, role guards, sub-dialogs and validation used by
 * the inline site-edit dialog in CustomersPage.js so both pages open the same
 * comprehensive editor.
 *
 * Props:
 *   siteId       (number)  — site to load and edit
 *   open         (bool)    — controlled open state
 *   onOpenChange (fn)      — called when the dialog requests close
 *   onSaved      (fn)      — called with the updated site object after a save
 *   onDetached   (fn)      — called after a successful detach-from-group
 *   siteName     (string)  — display name shown in the title while loading
 *   customerName (string)  — parent customer name used in email preview sample data
 */
import React, { useState, useCallback, useEffect } from 'react';
import api from '../services/api';
import {
  sitesService, sitePricingService, servicesService,
} from '../services';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import { getApiError } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from './ui/alert-dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { Checkbox } from './ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { RichTextEditor } from './ui/rich-text-editor';
import { EmailPreview } from './ui/email-preview';
import BudgetHistoryPanel from './BudgetHistoryPanel';
import {
  Loader2, Pencil, Trash2, Building2, Users, Banknote, Mail,
  Upload, ImageIcon, Unlink, X, Eye, Save, Edit3, Plus,
} from 'lucide-react';

// ── Constants (match CustomersPage) ───────────────────────────────────────────
const COUNTRIES = ['United Kingdom', 'United Arab Emirates'];

const AUDIT_FREQUENCIES = [
  { value: 'monthly',     label: 'Monthly' },
  { value: 'bi-monthly',  label: 'Bi-Monthly' },
  { value: 'quarterly',   label: 'Quarterly' },
  { value: 'semi-annual', label: 'Semi-Annual' },
  { value: 'annual',      label: 'Annual' },
  { value: 'adhoc',       label: 'Ad-hoc' },
  { value: 'custom',      label: 'Custom' },
];

// ── Main Component ────────────────────────────────────────────────────────────
export function SharedSiteEditDialog({
  siteId,
  open,
  onOpenChange,
  onSaved,
  onDetached,
  siteName: siteNameProp,
  customerName,
}) {
  const {
    isAdmin, canManageSites, canManageLogos, canEditSiteAuditorFields, isAuditor, isScheduler,
  } = useAuth();
  const { formatCurrency, currencySymbol, region, settings } = useSettings();
  const { toast } = useToast();

  const isAuditorOnly = isAuditor && !isScheduler && !isAdmin;
  const canEditPricing = isAdmin || canManageSites;
  const phonePlaceholder = region === 'UAE' ? '+971 50 123 4567' : '+44 7700 900000';

  // ── Core site form ─────────────────────────────────────────────────────────
  const [loading, setLoading]   = useState(false);
  const [saving, setSaving]     = useState(false);
  const [site, setSite]         = useState(null);        // loaded site object (has logo_url)
  const [form, setForm]         = useState({});

  // ── Site Contacts ──────────────────────────────────────────────────────────
  const [contacts,           setContacts]           = useState([]);
  const [contactDlgOpen,     setContactDlgOpen]     = useState(false);
  const [editingContact,     setEditingContact]     = useState(null);
  const [contactForm,        setContactForm]        = useState({
    name: '', email: '', phone: '', role: '', for_job_scheduling: false, for_job_reports: false,
  });

  // ── Pricing ────────────────────────────────────────────────────────────────
  const [pricing,            setPricing]            = useState([]);
  const [services,           setServices]           = useState([]);
  const [pricingDlgOpen,     setPricingDlgOpen]     = useState(false);
  const [editingPricing,     setEditingPricing]     = useState(null);
  const [pricingForm,        setPricingForm]        = useState({
    service_id: '', price: '', currency: region === 'UAE' ? 'AED' : 'GBP',
    is_default: false, description: '',
  });

  // ── Email Script ───────────────────────────────────────────────────────────
  const [emailDlgOpen,       setEmailDlgOpen]       = useState(false);
  const [tempEmailScript,    setTempEmailScript]     = useState('');
  const [showEmailPreview,   setShowEmailPreview]    = useState(false);

  // ── Detach ─────────────────────────────────────────────────────────────────
  const [detachOpen,         setDetachOpen]         = useState(false);
  const [detaching,          setDetaching]          = useState(false);

  // ── Load data ──────────────────────────────────────────────────────────────
  const loadAll = useCallback(async () => {
    if (!siteId || !open) return;
    setLoading(true);
    try {
      const [loadedSite, loadedContacts] = await Promise.all([
        sitesService.get(siteId),
        sitesService.listContacts(siteId),
      ]);
      setSite(loadedSite);
      setForm({
        name:                          loadedSite.name || '',
        is_active:                     loadedSite.is_active !== false,
        address_line_1:                loadedSite.address_line_1 || '',
        address_line_2:                loadedSite.address_line_2 || '',
        city:                          loadedSite.city || '',
        postcode:                      loadedSite.postcode || '',
        country:                       loadedSite.country || (region === 'UAE' ? 'United Arab Emirates' : 'United Kingdom'),
        audit_frequency:               loadedSite.audit_frequency || '',
        custom_frequency_days:         loadedSite.custom_frequency_days?.toString() || '',
        year_end_month:                loadedSite.year_end_month?.toString() || '',
        year_end_day:                  loadedSite.year_end_day?.toString() || '',
        budget_cost_of_sales_pct:      loadedSite.budget_cost_of_sales_pct?.toString() || '',
        days_stockholding_period:      loadedSite.days_stockholding_period?.toString() || '',
        food_budget_cost_of_sales_pct: loadedSite.food_budget_cost_of_sales_pct?.toString() || '',
        food_days_stockholding_period: loadedSite.food_days_stockholding_period?.toString() || '',
        custom_schedule_email_script:  loadedSite.custom_schedule_email_script || '',
      });
      setContacts(loadedContacts);

      if (canEditPricing) {
        const [loadedPricing, loadedServices] = await Promise.all([
          sitePricingService.getForSite(siteId),
          servicesService.list(),
        ]);
        setPricing(loadedPricing);
        setServices(loadedServices);
      }
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to load site data', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [siteId, open, canEditPricing, region, toast]);

  useEffect(() => {
    if (open) {
      loadAll();
    } else {
      // Reset on close
      setSite(null);
      setForm({});
      setContacts([]);
      setPricing([]);
      setContactDlgOpen(false);
      setPricingDlgOpen(false);
      setEmailDlgOpen(false);
      setDetachOpen(false);
    }
  }, [open, loadAll]);

  // ── Save site ──────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!form.name?.trim()) {
      toast({ title: 'Error', description: 'Site name is required', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const body = {
        name:                          form.name.trim(),
        is_active:                     form.is_active !== false,
        address_line_1:                form.address_line_1 || null,
        address_line_2:                form.address_line_2 || null,
        city:                          form.city || null,
        postcode:                      form.postcode || null,
        country:                       form.country || null,
        audit_frequency:               form.audit_frequency || null,
        custom_frequency_days:         form.audit_frequency === 'custom' && form.custom_frequency_days
          ? parseInt(form.custom_frequency_days, 10) : null,
        year_end_month:                form.year_end_month  ? parseInt(form.year_end_month,  10) : null,
        year_end_day:                  form.year_end_day    ? parseInt(form.year_end_day,    10) : null,
        budget_cost_of_sales_pct:      form.budget_cost_of_sales_pct      ? parseFloat(form.budget_cost_of_sales_pct)      : null,
        days_stockholding_period:      form.days_stockholding_period      ? parseInt(form.days_stockholding_period,      10) : null,
        food_budget_cost_of_sales_pct: form.food_budget_cost_of_sales_pct ? parseFloat(form.food_budget_cost_of_sales_pct) : null,
        food_days_stockholding_period: form.food_days_stockholding_period ? parseInt(form.food_days_stockholding_period, 10) : null,
        custom_schedule_email_script:  form.custom_schedule_email_script  || null,
      };

      let updated;
      if (isAuditorOnly) {
        updated = await sitesService.updateAuditorFields(siteId, {
          budget_cost_of_sales_pct:      body.budget_cost_of_sales_pct,
          days_stockholding_period:      body.days_stockholding_period,
          food_budget_cost_of_sales_pct: body.food_budget_cost_of_sales_pct,
          food_days_stockholding_period: body.food_days_stockholding_period,
          custom_schedule_email_script:  body.custom_schedule_email_script,
        });
      } else {
        updated = await sitesService.update(siteId, body);
      }

      toast({ title: 'Success', description: 'Site updated' });
      onSaved?.({ ...updated, logo_url: site?.logo_url });
      onOpenChange(false);
    } catch (err) {
      toast({ title: 'Error', description: getApiError(err, 'Failed to save site'), variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  // ── Contacts CRUD ──────────────────────────────────────────────────────────
  const openContactDlg = (contact = null) => {
    setEditingContact(contact);
    setContactForm(contact ? {
      name: contact.name || '', email: contact.email || '', phone: contact.phone || '',
      role: contact.role || '', for_job_scheduling: contact.for_job_scheduling || false,
      for_job_reports: contact.for_job_reports || false,
    } : { name: '', email: '', phone: '', role: '', for_job_scheduling: false, for_job_reports: false });
    setContactDlgOpen(true);
  };

  const handleSaveContact = async () => {
    if (!contactForm.name?.trim()) {
      toast({ title: 'Error', description: 'Contact name is required', variant: 'destructive' });
      return;
    }
    const data = {
      name:               contactForm.name,
      email:              contactForm.email  || null,
      phone:              contactForm.phone  || null,
      role:               contactForm.role   || null,
      for_job_scheduling: contactForm.for_job_scheduling,
      for_job_reports:    contactForm.for_job_reports,
    };
    try {
      if (editingContact) {
        await sitesService.updateContact(siteId, editingContact.id, data);
        toast({ title: 'Success', description: 'Contact updated' });
      } else {
        await sitesService.createContact(siteId, data);
        toast({ title: 'Success', description: 'Contact added' });
      }
      setContactDlgOpen(false);
      setContacts(await sitesService.listContacts(siteId));
    } catch (err) {
      toast({ title: 'Error', description: getApiError(err, 'Failed to save contact'), variant: 'destructive' });
    }
  };

  const handleDeleteContact = async (contact) => {
    try {
      await sitesService.deleteContact(siteId, contact.id);
      toast({ title: 'Success', description: 'Contact deleted' });
      setContacts(await sitesService.listContacts(siteId));
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to delete contact', variant: 'destructive' });
    }
  };

  // ── Pricing CRUD ───────────────────────────────────────────────────────────
  const openPricingDlg = (pricing = null) => {
    setEditingPricing(pricing);
    setPricingForm(pricing ? {
      service_id:  pricing.service_id?.toString() || '',
      price:       pricing.price?.toString()       || '',
      currency:    pricing.currency || (region === 'UAE' ? 'AED' : 'GBP'),
      is_default:  pricing.is_default || false,
      description: pricing.description || '',
    } : {
      service_id: '', price: '', currency: region === 'UAE' ? 'AED' : 'GBP',
      is_default: false, description: '',
    });
    setPricingDlgOpen(true);
  };

  const handleSavePricing = async () => {
    if (!pricingForm.service_id || !pricingForm.price) {
      toast({ title: 'Error', description: 'Service and price are required', variant: 'destructive' });
      return;
    }
    const data = {
      site_id:     siteId,
      service_id:  parseInt(pricingForm.service_id, 10),
      price:       parseFloat(pricingForm.price),
      currency:    pricingForm.currency,
      is_default:  pricingForm.is_default,
      description: pricingForm.description || null,
    };
    try {
      if (editingPricing) {
        await sitePricingService.update(editingPricing.id, data);
        toast({ title: 'Success', description: 'Pricing updated' });
      } else {
        await sitePricingService.create(data);
        toast({ title: 'Success', description: 'Pricing added' });
      }
      setPricingDlgOpen(false);
      setPricing(await sitePricingService.getForSite(siteId));
    } catch (err) {
      toast({ title: 'Error', description: getApiError(err, 'Failed to save pricing'), variant: 'destructive' });
    }
  };

  const handleDeletePricing = async (pricingItem) => {
    try {
      await sitePricingService.delete(pricingItem.id);
      toast({ title: 'Deleted', description: 'Pricing line removed' });
      setPricing(await sitePricingService.getForSite(siteId));
    } catch (err) {
      toast({ title: 'Error', description: getApiError(err, 'Cannot delete pricing'), variant: 'destructive' });
    }
  };

  // ── Detach from group ──────────────────────────────────────────────────────
  const handleDetach = async () => {
    if (!site) return;
    setDetaching(true);
    try {
      const result = await sitesService.detachFromGroup(siteId);
      toast({ title: 'Site Detached', description: result.message || `"${site.name}" is now an independent customer` });
      setDetachOpen(false);
      onOpenChange(false);
      onDetached?.();
    } catch (err) {
      toast({ title: 'Error', description: getApiError(err, 'Failed to detach site'), variant: 'destructive' });
    } finally {
      setDetaching(false);
    }
  };

  // ── Logo ───────────────────────────────────────────────────────────────────
  const handleLogoUpload = async (file) => {
    if (file.size > 5 * 1024 * 1024) {
      toast({ title: 'File too large (max 5MB)', variant: 'destructive' });
      return;
    }
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await api.post(`/sites/${siteId}/logo`, formData);
      setSite(prev => ({ ...prev, logo_url: res.data.logo_url }));
      toast({ title: 'Logo uploaded successfully' });
    } catch (err) {
      toast({ title: getApiError(err, 'Upload failed'), variant: 'destructive' });
    }
  };

  const handleLogoRemove = async () => {
    if (!window.confirm('Remove this logo?')) return;
    try {
      await api.delete(`/sites/${siteId}/logo`);
      setSite(prev => ({ ...prev, logo_url: null }));
      toast({ title: 'Logo removed' });
    } catch (err) {
      toast({ title: 'Failed to remove logo', variant: 'destructive' });
    }
  };

  // ── Shorthand for field props ──────────────────────────────────────────────
  const f = (field) => ({
    value:    form[field] || '',
    onChange: (e) => setForm(prev => ({ ...prev, [field]: e.target.value })),
    disabled: isAuditorOnly,
    className: isAuditorOnly ? 'bg-muted cursor-not-allowed' : '',
  });

  const displayName = form.name || siteNameProp || 'Site';

  // ──────────────────────────────────────────────────────────────────────────
  return (
    <>
      {/* ── Main Site Edit Dialog ─────────────────────────────────────────── */}
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>Edit Site Profile</DialogTitle>
            <DialogDescription>
              Manage site details, contact information, and pricing all in one place.
            </DialogDescription>
          </DialogHeader>

          {loading ? (
            <div className="py-12 flex items-center justify-center gap-2 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
              <span className="text-sm">Loading…</span>
            </div>
          ) : (
            <div className="grid gap-6 py-4 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 240px)' }}>

              {/* ── Site Details ─────────────────────────────────────────── */}
              <div className="space-y-4">
                <h4 className="text-sm font-semibold flex items-center gap-2 text-muted-foreground">
                  <Building2 className="h-4 w-4" /> Site Details
                  {isAuditorOnly && <Badge variant="outline" className="text-xs">View Only</Badge>}
                </h4>
                <div className="flex gap-4">
                  <div className="space-y-2 flex-1">
                    <Label>Site Name *</Label>
                    <Input
                      value={form.name || ''}
                      onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Site name"
                      disabled={isAuditorOnly}
                      className={isAuditorOnly ? 'bg-muted cursor-not-allowed' : ''}
                      data-testid="shared-site-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Status</Label>
                    <div className="flex items-center gap-3 h-10">
                      <Switch
                        checked={form.is_active !== false}
                        onCheckedChange={(v) => setForm(prev => ({ ...prev, is_active: v }))}
                        disabled={isAuditorOnly}
                        data-testid="shared-site-active-toggle"
                      />
                      <span className={`text-sm ${form.is_active !== false ? 'text-green-600' : 'text-muted-foreground'}`}>
                        {form.is_active !== false ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Address Line 1</Label>
                    <Input {...f('address_line_1')} placeholder="Street address" />
                  </div>
                  <div className="space-y-2">
                    <Label>Address Line 2</Label>
                    <Input {...f('address_line_2')} placeholder="Suite, building, etc." />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>City</Label>
                    <Input {...f('city')} placeholder="City" />
                  </div>
                  <div className="space-y-2">
                    <Label>Postcode</Label>
                    <Input {...f('postcode')} placeholder="Postcode" />
                  </div>
                  <div className="space-y-2">
                    <Label>Country</Label>
                    <Select
                      value={form.country || (region === 'UAE' ? 'United Arab Emirates' : 'United Kingdom')}
                      onValueChange={(v) => setForm(prev => ({ ...prev, country: v }))}
                      disabled={isAuditorOnly}
                    >
                      <SelectTrigger className={isAuditorOnly ? 'bg-muted cursor-not-allowed' : ''}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {COUNTRIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Audit Frequency</Label>
                    <Select
                      value={form.audit_frequency || 'not_set'}
                      onValueChange={(v) => setForm(prev => ({
                        ...prev,
                        audit_frequency:      v === 'not_set' ? '' : v,
                        custom_frequency_days: v !== 'custom' ? '' : prev.custom_frequency_days,
                      }))}
                      disabled={isAuditorOnly}
                    >
                      <SelectTrigger data-testid="shared-site-audit-frequency" className={isAuditorOnly ? 'bg-muted cursor-not-allowed' : ''}>
                        <SelectValue placeholder="Select frequency…" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="not_set">Not Set</SelectItem>
                        {AUDIT_FREQUENCIES.map(f => (
                          <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {form.audit_frequency === 'custom' && (
                      <div className="mt-2">
                        <Label className="text-xs text-muted-foreground">Days between audits</Label>
                        <Input
                          type="number" min="1" max="730"
                          placeholder="e.g. 45"
                          value={form.custom_frequency_days || ''}
                          onChange={(e) => setForm(prev => ({ ...prev, custom_frequency_days: e.target.value }))}
                          disabled={isAuditorOnly}
                          data-testid="shared-site-custom-frequency-days"
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* Year End */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Year End</Label>
                  <div className="flex gap-2">
                    <Select
                      value={form.year_end_month || ''}
                      onValueChange={(v) => setForm(prev => ({ ...prev, year_end_month: v, year_end_day: '' }))}
                      disabled={isAuditorOnly}
                    >
                      <SelectTrigger className="w-36" data-testid="shared-site-year-end-month">
                        <SelectValue placeholder="Month…" />
                      </SelectTrigger>
                      <SelectContent>
                        {['January','February','March','April','May','June',
                          'July','August','September','October','November','December']
                          .map((m, i) => <SelectItem key={i+1} value={String(i+1)}>{m}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Select
                      value={form.year_end_day || ''}
                      onValueChange={(v) => setForm(prev => ({ ...prev, year_end_day: v }))}
                      disabled={isAuditorOnly || !form.year_end_month}
                    >
                      <SelectTrigger className="w-24" data-testid="shared-site-year-end-day">
                        <SelectValue placeholder="Day…" />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.from({ length: 31 }, (_, i) => i + 1)
                          .map(d => <SelectItem key={d} value={String(d)}>{d}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    {(form.year_end_month || form.year_end_day) && !isAuditorOnly && (
                      <Button
                        type="button" size="sm" variant="outline"
                        className="h-9 px-2 text-xs text-muted-foreground hover:text-destructive hover:border-destructive"
                        onClick={() => setForm(prev => ({ ...prev, year_end_month: '', year_end_day: '' }))}
                        data-testid="shared-site-year-end-clear"
                      >
                        <X className="h-3.5 w-3.5 mr-1" />Clear
                      </Button>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">Used for YTD calculations in stocktake reports</p>
                </div>

                {/* Site Logo */}
                {canManageLogos && (
                  <div className="space-y-2 pt-2">
                    <Label className="text-sm font-semibold flex items-center gap-2 text-muted-foreground">
                      <ImageIcon className="h-4 w-4" /> Site Logo
                    </Label>
                    <div className="flex items-center gap-4">
                      {site?.logo_url ? (
                        <div className="relative group">
                          <StoredLogo
                            logoKey={site.logo_url}
                            alt="Site logo"
                            className="h-16 w-auto max-w-[160px] object-contain border rounded p-1 bg-white"
                            data-testid="shared-site-logo-preview"
                          />
                          <Button
                            size="icon" variant="destructive"
                            className="absolute -top-2 -right-2 h-5 w-5 opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={handleLogoRemove}
                            data-testid="shared-remove-site-logo"
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <label className="cursor-pointer">
                            <input
                              type="file"
                              accept=".png,.jpg,.jpeg,.svg,.webp"
                              className="hidden"
                              data-testid="shared-upload-site-logo-input"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) handleLogoUpload(file);
                                e.target.value = '';
                              }}
                            />
                            <Button variant="outline" size="sm" asChild>
                              <span data-testid="shared-upload-site-logo-btn">
                                <Upload className="h-3 w-3 mr-1" /> Upload Logo
                              </span>
                            </Button>
                          </label>
                          <span className="text-xs text-muted-foreground">PNG, JPG, SVG (max 5MB)</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <Separator />

              {/* ── Budget & Stockholding ─────────────────────────────────── */}
              <div className="space-y-4">
                <h4 className="text-sm font-semibold flex items-center gap-2 text-muted-foreground">
                  <Banknote className="h-4 w-4" /> Budget &amp; Stockholding
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Beverages Budget COS (%)</Label>
                    <Input
                      type="number" step="0.1" min="0" max="100"
                      value={form.budget_cost_of_sales_pct || ''}
                      onChange={(e) => setForm(prev => ({ ...prev, budget_cost_of_sales_pct: e.target.value }))}
                      placeholder="e.g. 25.5"
                      data-testid="shared-site-bev-cos-input"
                    />
                    <p className="text-xs text-muted-foreground">Beverages target cost of sales %</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Beverages Stockholding (Days)</Label>
                    <Input
                      type="number" min="0"
                      value={form.days_stockholding_period || ''}
                      onChange={(e) => setForm(prev => ({ ...prev, days_stockholding_period: e.target.value }))}
                      placeholder="e.g. 14"
                      data-testid="shared-site-bev-stockholding-input"
                    />
                    <p className="text-xs text-muted-foreground">Beverages stock holding days</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Food Budget COS (%)</Label>
                    <Input
                      type="number" step="0.1" min="0" max="100"
                      value={form.food_budget_cost_of_sales_pct || ''}
                      onChange={(e) => setForm(prev => ({ ...prev, food_budget_cost_of_sales_pct: e.target.value }))}
                      placeholder="e.g. 30.0"
                      data-testid="shared-site-food-cos-input"
                    />
                    <p className="text-xs text-muted-foreground">Food target cost of sales %</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Food Stockholding (Days)</Label>
                    <Input
                      type="number" min="0"
                      value={form.food_days_stockholding_period || ''}
                      onChange={(e) => setForm(prev => ({ ...prev, food_days_stockholding_period: e.target.value }))}
                      placeholder="e.g. 7"
                      data-testid="shared-site-food-stockholding-input"
                    />
                    <p className="text-xs text-muted-foreground">Food stock holding days</p>
                  </div>
                </div>
              </div>

              <Separator />

              {/* ── Custom Email Script ───────────────────────────────────── */}
              <div className="space-y-4">
                <h4 className="text-sm font-semibold flex items-center gap-2 text-muted-foreground">
                  <Mail className="h-4 w-4" /> Custom Email Script
                </h4>
                <div className="space-y-2">
                  <Label>Site-Specific Email Template (Optional)</Label>
                  <p className="text-xs text-muted-foreground mb-2">
                    If provided, this will override the organisation's default email script for this site only.
                  </p>
                  <div className="flex items-center gap-4">
                    <Button
                      variant="outline" size="sm"
                      onClick={() => {
                        setTempEmailScript(form.custom_schedule_email_script || '');
                        setShowEmailPreview(false);
                        setEmailDlgOpen(true);
                      }}
                      data-testid="shared-edit-site-email-script-btn"
                    >
                      <Edit3 className="h-3 w-3 mr-1" />Edit Email Script
                    </Button>
                    {form.custom_schedule_email_script ? (
                      <span className="text-xs text-muted-foreground">Custom script configured</span>
                    ) : (
                      <span className="text-xs text-amber-600">Using default from Settings</span>
                    )}
                  </div>
                </div>
              </div>

              <Separator />

              {/* ── Site Contacts ─────────────────────────────────────────── */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-semibold flex items-center gap-2 text-muted-foreground">
                    <Users className="h-4 w-4" /> Site Contacts ({contacts.length})
                  </h4>
                  <Button size="sm" variant="outline" onClick={() => openContactDlg()} data-testid="shared-add-site-contact-btn">
                    <Plus className="h-3 w-3 mr-1" /> Add Contact
                  </Button>
                </div>
                {contacts.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No contacts added yet</p>
                ) : (
                  <div className="space-y-2">
                    {contacts.map(contact => (
                      <div key={contact.id} className="flex items-center justify-between p-2 border rounded" data-testid={`shared-site-contact-row-${contact.id}`}>
                        <div className="flex items-center gap-3">
                          <div>
                            <div className="font-medium text-sm">{contact.name}</div>
                            <div className="text-xs text-muted-foreground">
                              {[contact.role, contact.email, contact.phone].filter(Boolean).join(' • ')}
                            </div>
                          </div>
                          <div className="flex gap-1">
                            {contact.for_job_scheduling && (
                              <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700">Sched. Pub. &amp; Billing</Badge>
                            )}
                            {contact.for_job_reports && (
                              <Badge variant="outline" className="text-xs bg-green-50 text-green-700">Report Recipient</Badge>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-1">
                          <Button type="button" variant="ghost" size="icon" className="h-7 w-7"
                            onClick={(e) => { e.stopPropagation(); openContactDlg(contact); }}>
                            <Pencil className="h-3 w-3" />
                          </Button>
                          <Button type="button" variant="ghost" size="icon" className="h-7 w-7"
                            onClick={(e) => { e.stopPropagation(); handleDeleteContact(contact); }}
                            data-testid={`shared-delete-site-contact-${contact.id}`}>
                            <Trash2 className="h-3 w-3 text-destructive" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* ── Pricing Lines (admin/scheduler only) ─────────────────── */}
              {canEditPricing && (
                <>
                  <Separator />
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-semibold flex items-center gap-2 text-muted-foreground">
                        <Banknote className="h-4 w-4" /> Pricing Lines
                      </h4>
                      <Button size="sm" variant="outline" onClick={() => openPricingDlg()} data-testid="shared-add-pricing-btn">
                        <Plus className="h-4 w-4 mr-1" /> Add Pricing
                      </Button>
                    </div>
                    {pricing.length > 0 ? (
                      <div className="border rounded-lg overflow-hidden">
                        <Table>
                          <TableHeader>
                            <TableRow className="bg-slate-50">
                              <TableHead className="text-xs">Service</TableHead>
                              <TableHead className="text-xs text-right">Price</TableHead>
                              <TableHead className="text-xs">Default</TableHead>
                              <TableHead className="text-xs text-right w-[80px]">Actions</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {pricing.map(p => (
                              <TableRow key={p.id}>
                                <TableCell className="text-sm">
                                  {p.service_name || services.find(s => s.id === p.service_id)?.name}
                                </TableCell>
                                <TableCell className="text-sm text-right font-medium">
                                  {formatCurrency(p.price)}
                                </TableCell>
                                <TableCell>
                                  {p.is_default && <Badge variant="secondary" className="text-[10px]">Default</Badge>}
                                </TableCell>
                                <TableCell className="text-right">
                                  <div className="flex justify-end gap-1">
                                    <Button variant="ghost" size="sm" onClick={() => openPricingDlg(p)}>
                                      <Pencil className="h-3 w-3" />
                                    </Button>
                                    <Button
                                      variant="ghost" size="sm" className="text-red-500 hover:text-red-700"
                                      onClick={() => handleDeletePricing(p)}
                                    >
                                      <Trash2 className="h-3 w-3" />
                                    </Button>
                                  </div>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    ) : (
                      <div className="text-center py-4 text-sm text-muted-foreground border rounded-lg bg-slate-50">
                        No pricing lines configured. Click "Add Pricing" to set service prices.
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* ── Budget History ────────────────────────────────────────── */}
              {siteId && (
                <div className="space-y-2 pt-4 border-t">
                  <BudgetHistoryPanel siteId={siteId} />
                </div>
              )}
            </div>
          )}

          <DialogFooter className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-2">
            <div className="flex gap-2 flex-wrap">
              {site?.parent_client_id && !isAuditorOnly && (
                <Button variant="outline" size="sm" onClick={() => setDetachOpen(true)} data-testid="shared-detach-from-group-btn">
                  <Unlink className="h-4 w-4 mr-1" /> Detach from Group
                </Button>
              )}
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving || loading} data-testid="shared-save-site-btn">
                {saving ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Saving…</> : 'Save Changes'}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Site Contact Sub-Dialog ─────────────────────────────────────────── */}
      <Dialog open={contactDlgOpen} onOpenChange={setContactDlgOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingContact ? 'Edit Contact' : 'Add Contact'}</DialogTitle>
            <DialogDescription>Contact details for {displayName}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                value={contactForm.name}
                onChange={(e) => setContactForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Contact name"
                data-testid="shared-site-contact-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Role</Label>
              <Input
                value={contactForm.role}
                onChange={(e) => setContactForm(prev => ({ ...prev, role: e.target.value }))}
                placeholder="e.g., Manager, Duty Manager"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={contactForm.email}
                  onChange={(e) => setContactForm(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="contact@site.com"
                />
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input
                  value={contactForm.phone}
                  onChange={(e) => setContactForm(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder={phonePlaceholder}
                />
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="shared-contact-scheduling"
                checked={contactForm.for_job_scheduling}
                onCheckedChange={(v) => setContactForm(prev => ({ ...prev, for_job_scheduling: v }))}
              />
              <Label htmlFor="shared-contact-scheduling" className="text-sm font-normal cursor-pointer">
                Receive Schedule Publication and Billing notifications
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="shared-contact-reports"
                checked={contactForm.for_job_reports}
                onCheckedChange={(v) => setContactForm(prev => ({ ...prev, for_job_reports: v }))}
              />
              <Label htmlFor="shared-contact-reports" className="text-sm font-normal cursor-pointer">
                Receive Job Report notifications
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setContactDlgOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveContact} data-testid="shared-save-site-contact-btn">
              {editingContact ? 'Update' : 'Add'} Contact
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Pricing Sub-Dialog ──────────────────────────────────────────────── */}
      <Dialog open={pricingDlgOpen} onOpenChange={setPricingDlgOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingPricing ? 'Edit Pricing' : 'Add Pricing'}</DialogTitle>
            <DialogDescription>For site: {displayName}</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label>Service *</Label>
              <Select
                value={pricingForm.service_id}
                onValueChange={(v) => setPricingForm(prev => ({ ...prev, service_id: v }))}
              >
                <SelectTrigger data-testid="shared-pricing-service-select">
                  <SelectValue placeholder="Select service" />
                </SelectTrigger>
                <SelectContent>
                  {services.filter(s => s.is_active).map(s => (
                    <SelectItem key={s.id} value={s.id.toString()}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Price ({currencySymbol}) *</Label>
              <Input
                type="number"
                value={pricingForm.price}
                onChange={(e) => setPricingForm(prev => ({ ...prev, price: e.target.value }))}
                placeholder="0.00"
                data-testid="shared-pricing-price-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                value={pricingForm.description}
                onChange={(e) => setPricingForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="e.g., Standard Rate"
              />
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="shared-pricing-is-default"
                checked={pricingForm.is_default}
                onCheckedChange={(v) => setPricingForm(prev => ({ ...prev, is_default: v }))}
              />
              <Label htmlFor="shared-pricing-is-default">Default pricing for this service</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPricingDlgOpen(false)}>Cancel</Button>
            <Button onClick={handleSavePricing} data-testid="shared-save-pricing-btn">
              {editingPricing ? 'Update' : 'Add'} Pricing
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Email Script Sub-Dialog ─────────────────────────────────────────── */}
      <Dialog open={emailDlgOpen} onOpenChange={(v) => { setEmailDlgOpen(v); if (!v) setShowEmailPreview(false); }}>
        <DialogContent
          className="flex flex-col dialog-resizable"
          style={{ width: '56rem', maxHeight: '90vh' }}
          data-testid="shared-site-email-script-dialog"
        >
          <DialogHeader>
            <DialogTitle>Edit Site Email Script</DialogTitle>
            <DialogDescription>
              Configure a custom email template for this site. Leave empty to use the default organisation template.
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto py-4 space-y-4">
            {!showEmailPreview ? (
              <>
                <RichTextEditor
                  value={tempEmailScript}
                  onChange={setTempEmailScript}
                  placeholder={`Dear {{contact_name}},\n\nPlease find below the scheduled audit details for {{site_name}}.\n\n{{jobs_table}}\n\nBest regards,\n{{organisation_name}}`}
                  minHeight="200px"
                />
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
                  <strong>Available placeholders</strong> (type these exactly as shown):
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    {[
                      ['{{contact_name}}', "Recipient's name"],
                      ['{{site_name}}', 'Site name'],
                      ['{{customer_name}}', 'Customer name'],
                      ['{{scheduled_date}}', 'First audit date'],
                      ['{{organisation_name}}', 'Your organisation'],
                      ['{{jobs_table}}', 'Schedule table'],
                    ].map(([code, desc]) => (
                      <div key={code} className="flex items-center gap-2">
                        <code className="bg-blue-100 px-2 py-0.5 rounded text-xs font-mono">{code}</code>
                        <span className="text-xs">{desc}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            ) : (
              <EmailPreview
                emailScript={tempEmailScript}
                organisationName={settings?.organisation_name || 'Your Organisation'}
                sampleData={{
                  site_name:     form.name     || displayName,
                  customer_name: customerName  || 'Sample Customer',
                }}
              />
            )}
          </div>
          <DialogFooter className="flex-shrink-0 gap-2 border-t pt-4">
            <Button variant="outline" onClick={() => setShowEmailPreview(p => !p)} data-testid="shared-toggle-site-preview-btn">
              <Eye className="h-4 w-4 mr-2" />
              {showEmailPreview ? 'Edit Script' : 'Preview Email'}
            </Button>
            <div className="flex-1" />
            <Button variant="outline" onClick={() => { setEmailDlgOpen(false); setShowEmailPreview(false); }}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                setForm(prev => ({ ...prev, custom_schedule_email_script: tempEmailScript || null }));
                setEmailDlgOpen(false);
                setShowEmailPreview(false);
                toast({ title: 'Script updated', description: 'Remember to save the site' });
              }}
              data-testid="shared-save-site-email-script-btn"
            >
              <Save className="h-4 w-4 mr-2" />Save Script
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Detach Confirm Dialog ───────────────────────────────────────────── */}
      <AlertDialog open={detachOpen} onOpenChange={setDetachOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Detach Site from Group?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove "{site?.name}" from its current group and create it as an independent customer.
              All job history, contacts, and settings will be preserved.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDetach} disabled={detaching} data-testid="shared-confirm-detach-btn">
              {detaching && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Detach
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export default SharedSiteEditDialog;
